/**
 * Delete a persona — a soft delete, said in words.
 *
 * `DELETE /api/builder/admin/users/{id}` sets `is_deleted`; the row and
 * its memberships stay. The dialog says so rather than implying the
 * person is gone, because "soft" is the difference between a
 * reactivation restoring their access and nobody knowing where it went.
 *
 * `ConfirmModal`, not `window.confirm`: the four `window.confirm` calls
 * on the pages this slice replaces are the grammar being retired, and a
 * browser dialog cannot say how many groups the person is in.
 */

import { ConfirmModal } from "@omnitok/ui";
import { useEffect, useState } from "react";

import { useT } from "@/i18n";
import type { UserAdmin } from "@/lib/admin-users-api";

export function DeletePersonModal({
  user,
  groups,
  onClose,
  onDeleted,
  remove,
}: {
  user: UserAdmin | null;
  /** How many groups they are in. Zero is worth saying too. */
  groups: number;
  onClose: () => void;
  onDeleted: () => void;
  remove: (id: string) => Promise<void>;
}): JSX.Element | null {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id ?? null;
  useEffect(() => {
    setError(null);
  }, [userId]);

  if (user === null) return null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await remove(user.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmModal
      open
      onClose={onClose}
      title={t("access.people.delete.title", { name: user.name })}
      confirmText={t("access.people.delete.confirm")}
      cancelText={t("common.cancel")}
      confirmVariant="danger"
      loading={busy}
      onConfirm={() => void submit()}
      message={
        <div className="space-y-2">
          <p className="text-sm text-neutral-700">
            {t("access.people.delete.body", { groups })}
          </p>
          <p className="text-[13px] text-neutral-600">
            {t("access.people.delete.soft")}
          </p>
          {error !== null && (
            <p className="whitespace-pre-wrap text-[13px] text-error-dark">
              {error}
            </p>
          )}
        </div>
      }
    />
  );
}
